import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import Switch from "react-switch";
import ModalLayout from "app/components/Layouts/ModalLayout/ModalLayout";
import Toast from "app/components/helpers/Toast";
import { WorkflowModel } from "app/models";
import closeIcon from "../../../../../../../public/images/close-icon.svg";
import styles from "./styles.module.scss";

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  workflow: WorkflowModel;
  onSave: (_params: Partial<WorkflowModel>) => Promise<any>;
  onDelete: () => void;
}

const WorkflowEditModal = ({
  isOpen,
  onClose,
  workflow,
  onSave,
  onDelete,
}: IProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState(workflow.name || "");
  const [enabled, setEnabled] = useState(workflow.enabled || false);
  const [isSaving, setIsSaving] = useState(false);
  const [toastSuccess, setToastSuccess] = useState<string | null>(null);
  const [toastError, setToastError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setName(workflow.name || "");
      setEnabled(workflow.enabled || false);
    }
  }, [isOpen, workflow.name, workflow.enabled]);

  const handleSave = () => {
    const trimmed = name.trim();
    if (!trimmed || isSaving) {
      return;
    }
    setIsSaving(true);
    onSave({ name: trimmed, enabled })
      .then(() => {
        setToastSuccess(t("Workflow updated"));
        onClose();
      })
      .catch(() => {
        setToastError(t("Something went wrong"));
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleDelete = () => {
    if (window.confirm(t("Are you sure you want to delete this workflow?"))) {
      onDelete();
      onClose();
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && name.trim() && !isSaving) {
      handleSave();
    }
  };

  return (
    <>
      <ModalLayout
        isOpen={isOpen}
        onClose={onClose}
        classNames={styles.modal_popup}
      >
        <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
          <div className={styles.modal_header}>
            <h2 className={styles.modal_title}>{t("Workflow Settings")}</h2>
            <img
              src={closeIcon}
              alt="close"
              className={styles.close_icon}
              onClick={onClose}
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>{t("Workflow Name")}</label>
            <input
              type="text"
              className={styles.input}
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={handleKeyDown}
              disabled={isSaving}
              autoFocus
            />
          </div>

          <div className={styles.field}>
            <label className={styles.label}>{t("Status")}</label>
            <div className={styles.toggle_row}>
              <span className={styles.toggle_label}>
                {enabled ? t("Active") : t("Inactive")}
              </span>
              <Switch
                checked={enabled}
                onChange={setEnabled}
                disabled={isSaving}
                offColor="#CECECE"
                onColor="#27A4FF"
                onHandleColor="#FFFFFF"
                handleDiameter={20}
                uncheckedIcon={false}
                checkedIcon={false}
                height={24}
                width={40}
              />
            </div>
          </div>

          <button
            type="button"
            className={styles.save_btn}
            onClick={handleSave}
            disabled={!name.trim() || isSaving}
          >
            {isSaving ? t("Saving...") : t("Save")}
          </button>

          <button
            type="button"
            className={styles.delete_btn}
            onClick={handleDelete}
            disabled={isSaving}
          >
            {t("Delete Workflow")}
          </button>
        </div>
      </ModalLayout>

      <Toast
        success={toastSuccess}
        error={toastError}
        onDone={() => {
          setToastSuccess(null);
          setToastError(null);
        }}
      />
    </>
  );
};

export default WorkflowEditModal;
